// const Cookies = require('js-cookie');
const dynamicConfig = require('./config/dynamic-config');
const DerivV2Auth = require('./services/deriv-v2-auth');

/*
 * Configuration values needed in js codes
 *
 * NOTE:
 * Please use the following command to avoid accidentally committing personal changes
 * git update-index --assume-unchanged src/javascript/config.js
 *
 */
const domain_app_ids = { // these domains also being used in '_common/url.js' as supported "production domains"
    'binary.com'                   : 1,
    'smarttrader.deriv.app'        : 22168,
    'smarttrader.deriv.com'        : 22168,
    'smarttrader.deriv.me'         : 27315,
    'smarttrader.deriv.be'         : 30768,
    'staging-smarttrader.deriv.be' : 31191,
    'staging-smarttrader.deriv.com': 22169,
    'binary.me'                    : 15284,
    'deriv.com'                    : 16929,
};

const getCurrentBinaryDomain = () =>
    Object.keys(domain_app_ids).find(domain => domain === window.location.hostname || `www.${domain}` === window.location.hostname);

const isProduction = () => {
    const all_domains = Object.keys(domain_app_ids).map(domain => {
        const is_on_subdomain = domain.match(/\./g).length === 2;
        return `${!is_on_subdomain ? 'www\\.' : ''}${domain.replace(/\./g, '\\.')}`;
    });
    return new RegExp(`^(${all_domains.join('|')})$`, 'i').test(window.location.hostname);
};

const binary_desktop_app_id = 14473;

const getAppId = () => {
    // Check URL parameter first (allows forcing a specific App ID)
    const urlParams = new URLSearchParams(window.location.search);
    const urlAppId = urlParams.get('app_id');
    if (urlAppId) {
        // eslint-disable-next-line no-console
        console.log('[SmartTrader] App ID found in URL:', urlAppId);
        window.localStorage.setItem('config.app_id', urlAppId);
        
        // Clean up URL if needed (optional, but keep it for now to avoid confusion on refresh)
        // const url = new URL(window.location);
        // url.searchParams.delete('app_id');
        // window.history.replaceState({}, '', url);

        return urlAppId;
    }

    // Try dynamic config next (for white-label sites)
    const dynamic_app_id = dynamicConfig.getAppId();
    if (dynamic_app_id && dynamic_app_id !== 22168) {
        return dynamic_app_id;
    }

    let app_id = null;
    const user_app_id   = ''; // you can insert Application ID of your registered application here
    const config_app_id = window.localStorage.getItem('config.app_id');
    const is_new_app    = /\/app\//.test(window.location.pathname);
    
    if (config_app_id) {
        // eslint-disable-next-line no-console
        console.log('[SmartTrader] Using stored config_app_id:', config_app_id);
        app_id = config_app_id;
    } else if (/desktop-app/i.test(window.location.href) || window.localStorage.getItem('config.is_desktop_app')) {
        window.localStorage.removeItem('config.default_app_id');
        window.localStorage.setItem('config.is_desktop_app', 1);
        app_id = binary_desktop_app_id;
    } else if (/staging\.binary\.com/i.test(window.location.hostname)) {
        window.localStorage.removeItem('config.default_app_id');
        app_id = is_new_app ? 16303 : 1098;
    } else if (/smarttrader-staging\.deriv\.app/i.test(window.location.hostname)) { // TODO: [app-link-refactor] - Remove backwards compatibility for `deriv.app`
        window.localStorage.removeItem('config.default_app_id');
        app_id = 22169;
    } else if (/staging-smarttrader\.deriv\.com/i.test(window.location.hostname)) {
        window.localStorage.removeItem('config.default_app_id');
        app_id = 22169;
    } else if (/staging-smarttrader\.deriv\.app/i.test(window.location.hostname)) { // TODO: [app-link-refactor] - Remove backwards compatibility for `deriv.app`
        window.localStorage.removeItem('config.default_app_id');
        app_id = 22169;
    } else if (user_app_id.length) {
        window.localStorage.setItem('config.default_app_id', user_app_id); // it's being used in endpoint chrome extension - please do not remove
        app_id = user_app_id;
    } else if (/localhost/i.test(window.location.hostname)) {
        app_id = 23709;
    } else {
        window.localStorage.removeItem('config.default_app_id');
        const current_domain = getCurrentBinaryDomain();
        // TODO: remove is_new_app && deriv.com check when repos are split
        app_id = (is_new_app && current_domain !== 'deriv.com') ? 22168 : (domain_app_ids[current_domain] || 22168);
    }

    return app_id;
};

const isBinaryApp = () => +getAppId() === binary_desktop_app_id;

const getAccountId = () => {
    // Check URL parameter first
    const urlParams = new URLSearchParams(window.location.search);
    const urlAccountId = urlParams.get('account_id');
    if (urlAccountId) {
        // Store and clean up URL
        window.localStorage.setItem('account_id', urlAccountId);
        const url = new URL(window.location);
        url.searchParams.delete('account_id');
        window.history.replaceState({}, '', url);
        return urlAccountId;
    }
    
    // Check localStorage
    const storedAccountId = window.localStorage.getItem('account_id');
    if (storedAccountId) {
        return storedAccountId;
    }
    
    // No account_id available
    return null;
};

const getAccountType = () => {
    // Check URL parameter first
    const urlParams = new URLSearchParams(window.location.search);
    const urlAccountType = urlParams.get('account_type');
    if (urlAccountType) {
        // Validate and store
        const validAccountType = ['demo', 'real'].includes(urlAccountType) ? urlAccountType : 'demo';
        window.localStorage.setItem('account_type', validAccountType);
        
        // Clean up URL
        const url = new URL(window.location);
        url.searchParams.delete('account_type');
        window.history.replaceState({}, '', url);

        return validAccountType;
    }
    
    // Check localStorage
    const storedAccountType = window.localStorage.getItem('account_type');
    if (storedAccountType && ['demo', 'real'].includes(storedAccountType)) {
        return storedAccountType;
    }
    
    // Default fallback
    return 'demo';
};

// Returns a Promise<string>. Authenticated URLs are one-shot OTP URLs.
// Documented public market-data endpoint: serves active_symbols, ticks/candles
// subscriptions, proposal and contracts_for WITHOUT auth. Deriv gates all of
// This is the only endpoint used for logged-out market data.
const PUBLIC_V2_WS_URL = 'wss://api.derivws.com/trading/v1/options/ws/public';

// Same truth ClientBase.isLoggedIn() uses; read directly from storage here to
// avoid a require cycle between config.js and _common/base/client_base.js.
const hasStoredSession = () =>
    !!(window.localStorage.getItem('account_id') && window.localStorage.getItem('account_type'));

// Every session key the legacy (v1) login flow ever wrote. Pre-migration users
// can carry these WITHOUT any v2 auth info — if we don't clear them the app
// keeps trying (and failing) to mint an authenticated socket forever.
const LEGACY_SESSION_KEYS = [
    'account_id',
    'account_type',
    'authToken',
    'active_loginid',
    'clientAccounts',
    'accountsList',
    'accountsList2',
    'current_account',
    'session_token',
    'is_api_token_login',
];

const clearStaleSessionState = () => {
    try {
        // Wipe any half-broken v2 state first (tokens, accounts, PKCE leftovers).
        DerivV2Auth.clearAll();
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[SmartTrader] Failed to clear v2 auth state:', e);
    }
    LEGACY_SESSION_KEYS.forEach((key) => {
        window.localStorage.removeItem(key);
        window.sessionStorage.removeItem(key);
    });
    // Everything that decides "is logged in" (ClientBase.isLoggedIn, the header)
    // reads the keys just cleared, but the React header captures the value at
    // mount — notify it so no half-logged-in zombie header survives.
    try {
        window.dispatchEvent(new CustomEvent('smarttrader:session-cleared'));
    } catch (e) {
        /* CustomEvent unavailable — header falls back to storage reads */
    }
};

// A DFunded account is signed in (dfunded_session_v1, shared with the other
// DForge sites). Read from storage here to avoid a require cycle with
// _common/base/dfunded/core.js. Ignored when DFunded is switched off for
// this site (features.dfunded === false).
const hasDFundedSession = () => {
    try {
        if (!dynamicConfig.isDFundedEnabled()) return false;
        const session = JSON.parse(window.localStorage.getItem('dfunded_session_v1') || 'null');
        return !!(session && session.account);
    } catch (e) {
        return false;
    }
};

const getSocketURL = async () => {
    // Trading goes to DFunded; only public market data comes from Deriv.
    if (hasDFundedSession()) return PUBLIC_V2_WS_URL;
    if (!hasStoredSession()) return PUBLIC_V2_WS_URL;

    let otpUrl = null;
    let hadValidToken = false;
    try {
        hadValidToken = !!(await DerivV2Auth.getValidAccessToken());
        if (hadValidToken) {
            otpUrl = await DerivV2Auth.getAuthenticatedWebSocketURL();
        }
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[SmartTrader] Authenticated WS URL resolution failed:', err);
    }
    if (otpUrl) return otpUrl;

    if (!hadValidToken) {
        // Stale legacy (v1-era) session keys with no recoverable v2 session
        // behind them — clear them so the app degrades to logged-out cleanly
        // instead of retrying a dead auth path forever.
        clearStaleSessionState();
    }
    // Valid token but OTP minting failed (network/API blip): keep the session,
    // serve public market data now; the next reconnect retries the OTP path.
    return PUBLIC_V2_WS_URL;
};

// Get public WebSocket URL for chart library (no authentication needed for market data)
const getChartSocketURL = () => {
    return PUBLIC_V2_WS_URL;
};

module.exports = {
    getCurrentBinaryDomain,
    isProduction,
    getAppId,
    isBinaryApp,
    getAccountType,
    getAccountId,
    getSocketURL,
    getChartSocketURL,
    hasDFundedSession,
    // Dynamic config exports for white-label support
    getDomainConfig    : dynamicConfig.getDomainConfig,
    getDomainConfigSync: dynamicConfig.getDomainConfigSync,
    initDynamicConfig  : dynamicConfig.initDynamicConfig,
    isFeatureEnabled   : dynamicConfig.isFeatureEnabled,
};
